import { Checkbox, Stack, Text, TextInput, Title } from '@mantine/core';
import { useNavigationContext } from '../contexts/NavigationContext';
import { usePresetContext } from '../contexts/PresetContext';
import { useState } from 'react';
import { useDebouncedCallback } from '@mantine/hooks';

type FileNameOptions = {
  useId: boolean;
  usePresetName: boolean;
  useSuffix: string;
};

const defaultOptions: FileNameOptions = {
  useId: false,
  usePresetName: false,
  useSuffix: '',
};

type WorkflowInfo = {
  id: number;
  name: string;
};

const getFilename = ({ useId, usePresetName, useSuffix } = defaultOptions, { id, name }: WorkflowInfo) => {
  let basename = 'asset-name';

  if (useId) {
    basename += `_${id}`;
  }
  if (usePresetName) {
    basename += `_${name}`;
  }

  if (useSuffix) {
    basename += `_${useSuffix}`;
  }

  return basename;
};

export const ExportSettings = () => {
  const { selectedTopNavigation } = useNavigationContext();
  const { workflow, updateWorkflowAttributes } = usePresetContext();
  const [customSuffix, setCustomSuffix] = useState(!!workflow.meta.filename?.useSuffix ? true : false);

  const debounceSuffixChange = useDebouncedCallback((value: string) => {
    onChange('useSuffix', value);
  }, 200);

  if (selectedTopNavigation !== 'export') {
    return null;
  }
  const filename = getFilename(workflow.meta.filename, { id: workflow.id, name: workflow.name });

  const onChange = (key: keyof FileNameOptions, value: string | boolean) => {
    updateWorkflowAttributes('meta', {
      ...workflow.meta,
      filename: {
        ...workflow.meta.filename,
        [key]: value,
      },
    });
  };

  const handleCustomSuffixChange = (value: boolean) => {
    setCustomSuffix(value);
    if (!value) {
      onChange('useSuffix', '');
    }
  };

  return (
    <Stack gap='sm' mt='lg'>
      <Title order={4}>File name settings</Title>
      <Checkbox
        label='Include model ID'
        checked={workflow.meta.filename?.useId ?? false}
        onChange={(event) => onChange('useId', event.target.checked)}
      />
      <Checkbox
        label='Include preset name'
        checked={workflow.meta.filename?.usePresetName ?? false}
        onChange={(event) => onChange('usePresetName', event.target.checked)}
      />
      <Checkbox
        label='Include custom suffix'
        checked={customSuffix}
        onChange={(event) => handleCustomSuffixChange(event.target.checked)}
      />
      {customSuffix && (
        <TextInput
          label='Custom suffix'
          defaultValue={workflow.meta.filename?.useSuffix ?? ''}
          onChange={(event) => debounceSuffixChange(event.currentTarget.value)}
        />
      )}
      <Title order={5}>File name preview</Title>
      <Text>{filename}</Text>
    </Stack>
  );
};
